import React, { useState, useMemo } from "react";
import "../SessionsTable/SessionsTable.css";
import "./FeedbacksTable.css";
import Pagination from "../../Common/Pagination";
import HighlightText from "../../Common/HighlightText";
import { IconChatBubblesBtn, IconEye, IconSearch } from "../../Common/EmcureReactIcons";

const FeedbacksTable = ({
  paginatedFeedbacks,
  currentFeedbackPage,
  feedbacksData,
  itemsPerPage,
  onPageChange,
  onViewFeedbackMessage,
  onViewMessages,
  feedbackSearchQuery,
  onFeedbackSearchInput,
  formatDate,
  loadingFeedbacks,
  dateRangePicker,
}) => {
  const [feedbackFilter, setFeedbackFilter] = useState("all"); // all | positive | negative

  const filteredFeedbacks = useMemo(() => {
    if (!Array.isArray(paginatedFeedbacks)) return [];

    if (feedbackFilter === "positive") {
      return paginatedFeedbacks.filter((f) => f.feedback === 1);
    }
    if (feedbackFilter === "negative") {
      return paginatedFeedbacks.filter((f) => f.feedback === -1);
    }
    return paginatedFeedbacks;
  }, [paginatedFeedbacks, feedbackFilter]);

  return (
    <>
      <div className="admin-title-wrapper">
        <div className="admin-title-left">
          <h2 className="admin-title">Feedbacks</h2>
          <p className="admin-title-sub">
            View and manage all user feedbacks
          </p>
        </div>
        <div className="admin-title-right">
          <div className="admin-title-actions">
            <div className="sessions-search-wrapper">
              <input
                type="text"
                className="sessions-search-input"
                placeholder="Search by User ID or Session ID..."
                value={feedbackSearchQuery}
                onChange={(e) => onFeedbackSearchInput(e.target.value)}
              />
              <IconSearch className="sessions-search-icon" width={16} height={16} />
            </div>
            {dateRangePicker}
          </div>
        </div>
      </div>
      <div className="admin-list-table-shell">
        <div className="sessions-table-header feedbacks-header">
          <div className="feedback-filter-group">
            <button
              type="button"
              className={`feedback-filter-btn ${
                feedbackFilter === "all" ? "active" : ""
              }`}
              onClick={() => setFeedbackFilter("all")}
            >
              All
            </button>
            <button
              type="button"
              className={`feedback-filter-btn ${
                feedbackFilter === "positive" ? "active" : ""
              }`}
              onClick={() => setFeedbackFilter("positive")}
            >
              Positive
            </button>
            <button
              type="button"
              className={`feedback-filter-btn ${
                feedbackFilter === "negative" ? "active" : ""
              }`}
              onClick={() => setFeedbackFilter("negative")}
            >
              Negative
            </button>
          </div>
        </div>
        <div className="admin-list-table-scroll data-table-container admin-dashboard-data-table-container">
          <div className="data-table-layout feedbacks-data-table-layout">
              {loadingFeedbacks ? (
                <div
                  style={{
                    padding: "40px",
                    textAlign: "center",
                    color: "#9CA3AF",
                  }}
                >
                  Loading feedbacks...
                </div>
              ) : filteredFeedbacks.length === 0 ? (
                <div
                  style={{
                    padding: "40px",
                    textAlign: "center",
                    color: "#9CA3AF",
                  }}
                >
                  No feedbacks found
                </div>
              ) : (
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Feedback ID</th>
                      <th>Session ID</th>
                      <th>Message ID</th>
                      <th>User ID</th>
                      <th>Feedback</th>
                      <th>Comment</th>
                      <th>Feedback Time</th>
                      <th className="actions-col">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredFeedbacks.map((feedback) => (
                      <tr key={feedback.feedback_id}>
                        <td>{feedback.feedback_id}</td>
                        <td>
                          <HighlightText 
                            text={feedback.session_id} 
                            highlight={feedbackSearchQuery}
                          />
                        </td>
                        <td>{feedback.message_id}</td>
                        <td>
                          <HighlightText 
                            text={feedback.user_id} 
                            highlight={feedbackSearchQuery}
                          />
                        </td>
                        <td>
                          <span
                            className={`feedback-badge ${
                              feedback.feedback === 1
                                ? "positive"
                                : feedback.feedback === -1
                                ? "negative"
                                : "neutral"
                            }`}
                          >
                            {feedback.feedback === 1
                              ? "Positive"
                              : feedback.feedback === -1
                              ? "Negative"
                              : "Neutral"}
                          </span>
                        </td>
                        <td className="feedback-comment-cell">
                          {String(
                            feedback.feedback_comment || feedback.comment || ""
                          ).trim() ? (
                            <HighlightText
                              text={String(
                                feedback.feedback_comment ||
                                  feedback.comment ||
                                  ""
                              ).trim()}
                              highlight={feedbackSearchQuery}
                            />
                          ) : (
                            "-"
                          )}
                        </td>
                        <td>{formatDate(feedback.feedback_time)}</td>
                        <td className="actions-cell">
                          <div className="actions-buttons">
                            <button
                              className="action-btn view-message-btn"
                              onClick={() => onViewFeedbackMessage(feedback)}
                              title="View Message Content"
                            >
                              <IconEye width={16} height={16} />
                            </button>
                            <button
                              className="view-messages-btn"
                              onClick={() =>
                                onViewMessages(
                                  feedback.session_id,
                                  feedback.user_id
                                )
                              }
                              title="View Chat History"
                            >
                              <IconChatBubblesBtn width={18} height={18} />
                              <span>Show Messages</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
          </div>
        </div>

        <div className="admin-table-pagination">
          <Pagination
            page={currentFeedbackPage}
            totalPages={feedbacksData.total_pages}
            totalItems={feedbacksData.total}
            itemsPerPage={itemsPerPage}
            onPageChange={onPageChange}
            showInfo={true}
          />
        </div>
      </div>
    </>
  );
};

export default FeedbacksTable;
